<template>
  <div>
    <bpmn-diagram-actions-shape-top-transform
      ref="changeShape"
      :selectedEvent="selectedEvent"
      :base-data="baseData"
      :svgGraph="svgGraph"
      :svgGroups="svgGroups"
      :svgLanes="svgLanes"
    >
      <template #buttons>
        <span></span>
      </template>
    </bpmn-diagram-actions-shape-top-transform>
    <div class="action-menu">
      <div class="action-button-group">

        <!-- transform shape -->
        <v-tooltip location="bottom" open-delay="500">
          <template v-slot:activator="{ props }">
            <v-menu density="compact" scrim="true">
              <template v-slot:activator="{ props: changeShapeProps }">
                <div
                  v-bind="{...props, ...(disableGatewayFromTransformMenu ? {} : changeShapeProps)}"
                  class="action-button"
                  :class="{'disabled': disableGatewayFromTransformMenu}"
                >
                  <v-icon v-bind="props" size="small">mds:category</v-icon>
                </div>
              </template>

              <v-list>
                <v-list-item
                  prepend-icon="custom:TaskIcon"
                  :active="selectedEvent.type === 'bpmn:Task' && !selectedEvent.subprocess"
                  @click="$refs.changeShape.transformShape('event')"
                >
                  <v-list-item-title>Event</v-list-item-title>
                </v-list-item>
                <v-list-item
                  prepend-icon="custom:DecisionIcon"
                  :active="selectedEvent.type === 'bpmn:ExclusiveGateway'"
                  @click="$refs.changeShape.transformShape('decision')"
                >
                  <v-list-item-title>Decision</v-list-item-title>
                </v-list-item>
                <v-list-item
                  prepend-icon="custom:SubworkflowIcon"
                  :active="selectedEvent.type === 'bpmn:Task' && !!selectedEvent.subprocess"
                  @click="$refs.changeShape.transformShape('subprocess')"
                >
                  <v-list-item-title>Subprocess</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>

          <template #default>
            <div>
              <p
                v-if="!disableGatewayFromTransformMenu"
                class="text-body-2 mb-1"
              >
                Change shape
              </p>
              <p v-else class="text-body-2 mb-1">
                Change shape (disabled because of multiple connections)
              </p>
            </div>
          </template>
        </v-tooltip>

        <!-- delete connection -->
        <v-tooltip location="bottom" open-delay="500">
          <template v-slot:activator="{ props }">
            <div
              v-bind="props"
              class="action-button"
              :disabled="isLeafNode"
              @click="initiateDeleteConnection"
            >
              <v-icon
                size="small"
                :class="{'captures-description': hasRemovableConnection == 1}"
              >mds:keyboard-tab
              </v-icon>
            </div>
          </template>
          <div>
            <p v-if="isLeafNode">
              Delete connection (disabled, no connection can be deleted)
            </p>
            <p v-else-if="hasRemovableConnection > 0" class="text-body-2 mb-1">
              Delete connection
            </p>
            <p v-else class="text-body-2 mb-1">
              Delete connection (can create a new starting point)
            </p>
          </div>
        </v-tooltip>

        <!-- move down in event group -->
        <v-tooltip location="bottom" open-delay="500">
          <template v-slot:activator="{ props }">
            <div
              v-bind="props"
              class="action-button"
              :class="{'disabled': !atTheBottomOfLane}"
              @click="atTheBottomOfLane && moveDownInEventGroup()"
            >
              <v-icon
                size="small"
                class="captures-description"
              >
                mds:arrow-downward
              </v-icon>
            </div>
          </template>
          <div>
            <p v-if="atTheBottomOfLane" class="text-body-2 mb-1">
              Expand lane and move down this shape
            </p>
            <p v-else class="text-body-2 mb-1">
              Expand lane disabled (use drag & drop to move down)
            </p>
          </div>
        </v-tooltip>

        <bpmn-delete-connection-dialog
          :open="confirmDialog"
          :connections="connections"
          @close="confirmDialog = false"
          @delete-connection="deleteConnection"
        ></bpmn-delete-connection-dialog>

        <!-- change color -->
        <workflow-settings-requirement-type-color-picker
          appearance="diagram"
          variant="events"
          :color="color"
          @change="changeColor"
        >
          <template #activator="{ props: onMenu }">
            <v-tooltip location="bottom" open-delay="500">
              <template v-slot:activator="{ props: tooltipActivator }">
                <div
                  v-bind="onMenu"
                  class="action-button justify-center">
                  <!-- eslint-disable vue/valid-v-on vue/no-parsing-error -->
                  <v-avatar
                    size="14"
                    class="m-auto color-item"
                    :color="color"
                    v-bind="tooltipActivator"
                  ></v-avatar>
                  <!-- eslint-enable vue/valid-v-on vue/no-parsing-error -->
                </div>
              </template>

              <div>
                <p class="text-body-2 mb-1">
                  Select color
                </p>
              </div>
            </v-tooltip>
          </template>
        </workflow-settings-requirement-type-color-picker>

        <!-- delete event -->
        <v-tooltip location="bottom" open-delay="500">
          <template v-slot:activator="{ props }">
            <div
              v-bind="{ ...props }"
              class="action-button"
              :class="{'disabled': !$refs.deleteItemDialog?.eventCanBeDeleted}"
              deleteItemDialog
              @click="$refs.deleteItemDialog.openDialog()"
            >
              <v-icon size="small">mds:delete</v-icon>
            </div>
          </template>

          <div v-if="$refs.deleteItemDialog?.eventCanBeDeleted">
            <p class="text-body-2 mb-1">Delete</p>
            <p class="mb-0">
              <code>ALT/⌥ + BACKSPACE</code> - Delete selected event
            </p>
          </div>
          <div v-else>
            <p class="text-body-2 mb-1">Delete (disabled)</p>
            <p class="mb-0">
              You cannot remove this event because there is more than one event attached after it.
            </p>
          </div>
        </v-tooltip>
      </div>
      <workflow-delete-item-dialog
        ref="deleteItemDialog"
        :itemType="'Event'"
        :hideIcon="true"
      ></workflow-delete-item-dialog>

      <!-- add event -->
      <div
        class="action-button-group"
        :data-test-actions-container="selectedEvent.name"
      >
        <v-tooltip location="bottom" open-delay="500">
          <template v-slot:activator="{ props }">
            <div
              role="button"
              v-bind="props"
              class="action-button captures-description"
              @click="$emit('add-shape', { type: 'bpmn:Task' })"
            >
              <v-icon size="small">custom:TaskIcon</v-icon>
            </div>
          </template>

          <template #default>
            <div>
              <p class="text-body-2 mb-1">Add event</p>
              <p class="mb-0">
                <code>ALT/⌥ + ARROW RIGHT/UP/DOWN</code>
              </p>
            </div>
          </template>
        </v-tooltip>
      </div>

      <!-- add decision -->
      <div class="action-button-group">
        <v-tooltip location="bottom" open-delay="500">
          <template v-slot:activator="{ props }">
            <button
              v-bind="props"
              class="action-button captures-description"
              @click="$emit('add-shape', { type: 'bpmn:ExclusiveGateway' });  $event.currentTarget.blur();"
            >
              <v-icon size="small">custom:DecisionIcon</v-icon>
            </button>
          </template>

          <template #default>
            <div>
              <p class="text-body-2 mb-1">Add decision</p>
            </div>
          </template>
        </v-tooltip>
      </div>

      <!-- add connection -->
      <div class="action-button-group">
        <v-tooltip location="bottom" open-delay="500">
          <template v-slot:activator="{ props }">
            <button
              :disabled="addConnectionDisabled"
              :class="enableConnectionTargets ? 'active' : ''"
              @click="eventBusEmitEnableTargets(); $event.currentTarget.blur();"
            >
              <v-icon v-bind="props" size="small">custom:ConnectionIcon</v-icon>
            </button>
          </template>

          <template #default>
            <div>
              <p v-if="addConnectionDisabled" class="text-body-2 mb-1">
                Disabled (this event already has a connection)
              </p>
              <p v-else class="text-body-2 mb-1">Add connection</p>
            </div>
          </template>
        </v-tooltip>
      </div>

      <!-- add subprocess -->
      <div class="action-button-group">
        <v-tooltip location="bottom" open-delay="500">
          <template v-slot:activator="{ props: tooltipActivator }">
            <bpmn-diagram-actions-shape-top-transform
              :selectedEvent="selectedEvent"
              :base-data="baseData"
              :svgGraph="svgGraph"
              :svgGroups="svgGroups"
              :svgLanes="svgLanes"
              @add-shape="$emit('add-shape', $event)"
              class="text-center"
            >
              <template v-slot:buttons="props">
                <button v-bind="tooltipActivator" @click="props.openDialog()">
                  <v-icon style="width: 22px; height: 20px" size="small"
                  >custom:SubworkflowIcon</v-icon
                  >
                </button>
              </template>
            </bpmn-diagram-actions-shape-top-transform>
          </template>

          <template #default>
            <div>
              <p class="text-body-2 mb-1">Add subprocess</p>
            </div>
          </template>
        </v-tooltip>
      </div>

      <!-- add event with AI -->
      <div class="action-button-group">
        <v-tooltip location="bottom" open-delay="500">
          <template v-slot:activator="{ props: tooltipActivator }">
            <button
              v-bind="tooltipActivator"
              @click="$emit('add-shape-ai', 'bpmn:Task', selectedEvent.id); $event.currentTarget.blur();">
              <v-icon style="width: 22px; height: 20px" size="small"
              >mds:temp-preferences-custom</v-icon
              >
            </button>
          </template>

          <template #default>
            <div>
              <p class="text-body-2 mb-1">Add event with AI</p>
            </div>
          </template>
        </v-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import BpmnDiagramActionsShapeTopTransform from '@/components/BpmnDiagramActionsShapeTopTransform'
import WorkflowSettingsRequirementTypeColorPicker from '@/components/WorkflowSettingsRequirementTypeColorPicker'
import { workflowApiHelpers } from '@/api/workflowApiHelpers'
import { EventBus } from '@/event-bus/event-bus.js'
import workflowApi from '@/api/workflowApi'
import BpmnDeleteConnectionDialog from '@/components/BpmnDeleteConnectionDialog'
import { diagramHelpers } from '@/mixins/diagramHelpers.js'
import WorkflowDeleteItemDialog from '@/components/WorkflowDeleteItemDialog'

export default {
  components: {
    BpmnDiagramActionsShapeTopTransform,
    WorkflowSettingsRequirementTypeColorPicker,
    BpmnDeleteConnectionDialog,
    WorkflowDeleteItemDialog
  },
  mixins: [diagramHelpers],
  props: {
    selectedEvent: Object,
    enableConnectionTargets: Boolean,
    active: Boolean,
    color: String,
    events: Array,
    baseData: Object,
    svgGraph: Object,
    svgGroups: Object,
    svgLanes: Object,
    workflow: Object
  },

  data () {
    return {
      connections: [],
      confirmDialog: false
    }
  },

  methods: {
    changeColor (color) {
      const selectedEvent = this.svgGraph[this.$route.params.eventId]
      selectedEvent.color = { ...color, rgb: `rgb(${color.red},${color.green},${color.blue})` }
      workflowApi.updateWorkflow({
        svgGraph: this.svgGraph,
        svgLanes: this.svgLanes,
        workflow: this.workflow,
        updateNote: {
          action: 'changed color of',
          target: selectedEvent.name
        }
      }, true)
    },
    initiateDeleteConnection () {
      if (this.isLeafNode) return
      const parentId = this.$route.params.eventId
      this.connections = []
      const childIds = this.svgGraph[parentId].childIds
      if (childIds.length > 1) {
        this.showConfirmationDialog(childIds)
      } else {
        this.deleteConnection(parentId, childIds[0])
      }
    },
    showConfirmationDialog (childIds) {
      childIds.forEach(childId => {
        const target = this.svgGraph[childId]
        this.connections.push({ parentId: this.$route.params.eventId, childId, eventName: target.name })
      })
      this.confirmDialog = true
    },
    eventBusEmitEnableTargets () {
      EventBus.$emit('enable-connection-targets', !this.enableConnectionTargets)
    },
    deleteConnection (parentId, childId) {
      this.confirmDialog = false
      workflowApi.deleteConnectionFromEvent(parentId, childId, this.svgGraph, this.svgLanes, this.svgGroups, this.workflow)
    },
    moveDownInEventGroup () {
      this.svgGraph[this.$route.params.eventId].offset++
      workflowApi.updateWorkflow({
        svgGraph: this.svgGraph,
        svgLanes: JSON.parse(JSON.stringify(this.svgLanes)),
        svgGroups: this.svgGroups,
        workflow: this.workflow,
        updateNote: {
          action: 'moved down event',
          target: this.svgGraph[this.$route.params.eventId].name
        }
      })
    }
  },

  computed: {
    isLeafNode () {
      return !this.svgGraph[this.$route.params.eventId]?.childIds?.length
    },
    addConnectionDisabled () {
      const res = (this.selectedEvent?.childIds?.length > 0) && (this.selectedEvent?.type !== 'bpmn:ExclusiveGateway')
      return res
    },
    hasRemovableConnection () {
      return workflowApiHelpers.getRemovableSequenceFlows(this.events, this.selectedEvent.id).length
    },
    atTheBottomOfLane () {
      return this.currentEventIsAtTheBottomOfItsLane(this.svgLanes, this.svgGraph, this.selectedEvent.id)
    },
    disableGatewayFromTransformMenu () {
      return Array.isArray(this.selectedEvent.childIds) && this.selectedEvent.childIds.length > 1
    }
  },

  watch: {
    confirmDialog: function (val) {
      if (!val) {
        this.connections = []
      }
    }
  }
}
</script>

<style scoped>
.action-menu {
  display: flex;
  justify-content: space-around;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 8px;
  background-color: #FCFCFC;
  box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.15);
}

.action-button-group {
  display: flex;
  justify-content: space-around;
  gap: 8px;
}

.action-button-group:not(:first-child) {
  padding-left: 8px;
  border-left: 1px solid #E5E2E1;
}

.action-button-group > span {
  display: none;
}

.action-button {
  padding: 0;
  margin: -4px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  border-radius: 4px;
  background-color: var(--light-1);
  transition: background-color ease 0.2s;
}

.action-button:hover {
  background-color: var(--light-2);
}

.action-button:active {
  background-color: var(--light-3);
}

.action-button.active {
  background-color: var(--light-3);
}

.action-button:hover.active {
  background-color: var(--light-4);
}

.action-button.disabled {
  background-color: var(--light-1);
}

.action-button > .v-icon {
  font-size: 20px;
  margin: auto;
  color: #363A40 !important;
}

.action-button.disabled > .v-icon {
  color: #dadee4 !important;
}
</style>
